import "@/App.css";
import "@/index.dark.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { initTelemetry } from "@/lib/telemetry";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Security from "@/pages/Security";
import About from "@/pages/About";
import Professionals from "@/pages/Professionals";
import PublicHelp from "@/pages/PublicHelp";
import Contact from "@/pages/Contact";
import Pricing from "@/pages/Pricing";
import Beta from "@/pages/Beta";
import {
  FeaturesOverview,
  FeatureCalendar,
  FeatureExpenses,
  FeatureMessaging,
  FeatureCourtBundle,
  FeatureJournal,
} from "@/pages/FeaturePages";
import Verify from "@/pages/Verify";
import MediatorSignup from "@/pages/MediatorSignup";
import { VerifyEmail, VerifyPending } from "@/pages/VerifyEmail";
import AppShell from "@/layouts/AppShell";
import Dashboard from "@/pages/Dashboard";
import Messages from "@/pages/Messages";
import CalendarPage from "@/pages/CalendarPage";
import Expenses from "@/pages/Expenses";
import CourtOrders from "@/pages/CourtOrders";
import UpcomingExpensesPage from "@/pages/UpcomingExpensesPage";
import Maintenance from "@/pages/Maintenance";
import Insights from "@/pages/Insights";
import Journal from "@/pages/Journal";
import Documents from "@/pages/Documents";
import Children from "@/pages/Children";
import Settings from "@/pages/Settings";
import Requests from "@/pages/Requests";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminAuditLogPage from "@/pages/AdminAuditLogPage";
import ConnectionsPage from "@/pages/ConnectionsPage";
import AdminInactivePage from "@/pages/AdminInactivePage";
import SecurityDashboard from "@/pages/SecurityDashboard";
import TrustComplianceCentre from "@/pages/TrustComplianceCentre";
import TrustCentre from "@/pages/TrustCentre";
import DueDiligence from "@/pages/DueDiligence";
import ContactVisits from "@/pages/ContactVisits";
import ChildExpenses from "@/pages/ChildExpenses";
import UpgradePage from "@/pages/UpgradePage";
import YearlyPerksPage from "@/pages/YearlyPerksPage";
import OnboardingFlow from "@/pages/OnboardingFlow";
import HelpPage from "@/pages/HelpPage";
import ActivityPage from "@/pages/ActivityPage";
import CourtBundle from "@/pages/CourtBundle";
import CourtBundleHistory from "@/pages/CourtBundleHistory";
import ExportsInbox from "@/pages/ExportsInbox";
import ThirdPartyDashboard from "@/pages/ThirdPartyDashboard";
import ThirdPartyAccessPage from "@/pages/ThirdPartyAccessPage";
import FacilitatorPortal from "@/pages/FacilitatorPortal";
import { PdfDownloadProvider } from "@/lib/PdfDownloadProvider";
import { ChildScopeProvider } from "@/context/ChildScopeContext";
import CookieConsent from "@/components/CookieConsent";
import PublicAnalytics from "@/components/PublicAnalytics";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import RuntimeBanner from "@/components/RuntimeBanner";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6F828A]">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};

function App() {
  initTelemetry();
  return (
    <AuthProvider>
      <ThemeProvider>
        <ChildScopeProvider>
          <PdfDownloadProvider>
            <BrowserRouter>
              <PublicAnalytics />
              <RuntimeBanner />
              <ImpersonationBanner />
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/security" element={<Security />} />
                <Route path="/trust" element={<TrustCentre />} />
                <Route path="/trust/due-diligence" element={<DueDiligence />} />
                <Route path="/about" element={<About />} />
                <Route path="/professionals" element={<Professionals />} />
                <Route path="/help" element={<PublicHelp />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/beta" element={<Beta />} />
                {/* v77.99.14 — Marketing landing pages for individual features.
                    Deliberately NOT linked from the main nav: these URLs are for
                    pasting into paid ads, articles, and partner sites. Each
                    page sets its own SEO meta + drives a single CTA pair
                    (Start free trial / See pricing). */}
                <Route path="/features" element={<FeaturesOverview />} />
                <Route path="/features/calendar" element={<FeatureCalendar />} />
                <Route path="/features/expenses" element={<FeatureExpenses />} />
                <Route path="/features/messaging" element={<FeatureMessaging />} />
                <Route path="/features/court-bundle" element={<FeatureCourtBundle />} />
                <Route path="/features/journal" element={<FeatureJournal />} />
                <Route path="/verify" element={<Verify />} />
                <Route path="/mediator-signup" element={<MediatorSignup />} />
                {/* v77.99.108 (iter 122.m4b) — Public, unauthenticated
                    Third-Party Facilitator submission portal. The
                    magic-link `/facilitator/:token` is the ONLY way
                    in. Deliberately outside `/app/*` and outside any
                    Protected wrapper because the observer has no
                    account. The token itself is the credential. */}
                <Route path="/facilitator/:token" element={<FacilitatorPortal />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/verify-pending" element={<Protected><VerifyPending /></Protected>} />
                {/* v77.49 — Third-party (mediator/solicitor) dashboard.
                    Uses Protected (login required) but renders its own
                    chrome — no AppShell — because the third-party UX is
                    deliberately distinct from the parent UX. */}
                <Route path="/app/third-party" element={<Protected><ThirdPartyDashboard /></Protected>} />
                <Route path="/app/onboarding" element={<Protected><OnboardingFlow /></Protected>} />
                <Route path="/app" element={<Protected><AppShell /></Protected>}>
                  <Route index element={<Dashboard />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="messages" element={<Messages />} />
                  <Route path="calendar" element={<CalendarPage />} />
                  <Route path="contact-visits" element={<ContactVisits />} />
                  <Route path="requests" element={<Requests />} />
                  <Route path="expenses" element={<Expenses />} />
                  <Route path="court-orders" element={<CourtOrders />} />
                  <Route path="upcoming-expenses" element={<UpcomingExpensesPage />} />
                  <Route path="maintenance" element={<Maintenance />} />
                  <Route path="insights" element={<Insights />} />
                  <Route path="journal" element={<Journal />} />
                  <Route path="documents" element={<Documents />} />
                  <Route path="children" element={<Children />} />
                  <Route path="children/:id/expenses" element={<ChildExpenses />} />
                  <Route path="court-bundle" element={<CourtBundle />} />
                  <Route path="court-bundle/history" element={<CourtBundleHistory />} />
                  <Route path="exports" element={<ExportsInbox />} />
                  <Route path="third-party-access" element={<ThirdPartyAccessPage />} />
                  <Route path="activity" element={<ActivityPage />} />
                  <Route path="help" element={<HelpPage />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="admin" element={<AdminDashboard />} />
                  <Route path="admin/inactive" element={<AdminInactivePage />} />
                  <Route path="admin/audit-log" element={<AdminAuditLogPage />} />
                  <Route path="admin/security" element={<SecurityDashboard />} />
                  <Route path="admin/trust" element={<TrustComplianceCentre />} />
                  <Route path="settings/connections" element={<ConnectionsPage />} />
                  <Route path="upgrade" element={<UpgradePage />} />
                  <Route path="upgrade/yearly" element={<YearlyPerksPage />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <CookieConsent />
            </BrowserRouter>
            <Toaster position="top-right" />
          </PdfDownloadProvider>
        </ChildScopeProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
